import React from 'react';
import { Box, Typography } from '@mui/material';
import { ImageOutlined as ImageOutlinedIcon } from '@mui/icons-material';

export interface ProductImagePlaceholderProps {
  /**
   * `fill` — occupies the parent (absolute inset 0), for grid thumbnails.
   * `box` — fixed square size, for detail headers / compact previews.
   */
  variant?: 'fill' | 'box';
  /**
   * Side length in px when variant is `box`.
   * Also used as a size hint for icon/label when variant is `fill` (default 96).
   */
  size?: number;
  borderRadius?: number | string;
  showLabel?: boolean;
  className?: string;
}

const ProductImagePlaceholder: React.FC<ProductImagePlaceholderProps> = ({
  variant = 'box',
  size = 96,
  borderRadius,
  showLabel = true,
  className,
}) => {
  const isFill = variant === 'fill';
  const iconSize = Math.max(22, size * 0.38);
  const labelFontSize = size < 80 ? '0.65rem' : isFill ? '0.75rem' : Math.max(10, size * 0.12);

  return (
    <Box
      className={className}
      aria-label="Sin foto"
      sx={{
        ...(isFill
          ? {
              position: 'absolute',
              inset: 0,
            }
          : {
              width: size,
              height: size,
              borderRadius: borderRadius ?? 1,
              border: '1px solid',
              borderColor: 'divider',
            }),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.35,
        backgroundColor: (theme) =>
          theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100],
        color: 'text.disabled',
        userSelect: 'none',
        pointerEvents: 'none',
      }}
    >
      <ImageOutlinedIcon sx={{ fontSize: iconSize }} />
      {showLabel && (
        <Typography
          component="span"
          sx={{
            fontSize: labelFontSize,
            fontWeight: 600,
            lineHeight: 1.2,
            color: 'text.disabled',
          }}
        >
          Sin foto
        </Typography>
      )}
    </Box>
  );
};

export default ProductImagePlaceholder;
